import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, Play, RotateCcw, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

export const InteractiveDemo = () => {
  const navigate = useNavigate();
  const [demoProduct, setDemoProduct] = useState({
    name: 'KaufPause Premium',
    price: 16.90,
    daysLeft: 30,
    status: 'waiting' as 'waiting' | 'bought' | 'skipped',
    isSimulating: false
  });
  const [showResult, setShowResult] = useState(false);

  const startSimulation = () => {
    setDemoProduct(prev => ({ ...prev, isSimulating: true, daysLeft: 30 }));
    setShowResult(false);
    
    // Schnellere Simulation für bessere UX
    const simulateDay = (currentDay: number) => {
      if (currentDay > 30) return;
      
      setTimeout(() => {
        setDemoProduct(prev => {
          const newDaysLeft = 30 - currentDay;
          
          if (newDaysLeft <= 0) {
            return { ...prev, daysLeft: 0, isSimulating: false };
          }
          
          return { ...prev, daysLeft: newDaysLeft };
        });
        
        if (currentDay < 30) {
          simulateDay(currentDay + 1);
        }
              }, 250); // Langsamere Animation für bessere Nachvollziehbarkeit
    };
    
    simulateDay(1);
  };

  const handleDecision = (decision: 'bought' | 'skipped') => {
    setDemoProduct(prev => ({ ...prev, status: decision }));
    setShowResult(true);
    
    if (decision === 'bought') {
      // Subtile Puls-Animation für "Gekauft" - kein Konfetti
      const button = document.querySelector(`[data-decision="${decision}"]`);
      if (button) {
        button.classList.add('animate-pulse');
        setTimeout(() => button.classList.remove('animate-pulse'), 1000);
      }
    } else if (decision === 'skipped') {
      // Konfetti für "Gespart" 
        confetti({
        particleCount: 50,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10B981', '#059669', '#34D399'],
        });
    }
  };

  const resetDemo = () => {
    setDemoProduct({
      name: 'KaufPause Premium',
      price: 16.90,
      daysLeft: 30,
      status: 'waiting',
      isSimulating: false
    });
    setShowResult(false);
  };

  return (
    <Card className="border-2 border-gray-200 shadow-xl bg-white">
        <CardHeader className="text-center pb-3 px-4 sm:px-6">
          <CardTitle className="text-lg sm:text-xl font-bold text-gray-900">
            Ist KaufPause das Richtige für dich?
          </CardTitle>
          <p className="text-gray-600 text-xs sm:text-sm">
            Teste die 30-Tage-Regel mit unserem Premium-Angebot
          </p>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 md:p-8 pt-0">
          {/* Demo Product */}
          <div className={`p-4 sm:p-6 border-2 rounded-xl transition-all duration-500 ${
            demoProduct.status === 'bought' ? 'bg-blue-50 border-blue-200' :
            demoProduct.status === 'skipped' ? 'bg-green-50 border-green-200' :
            'bg-white border-gray-200 hover:border-emerald-200'
          }`}>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
              <div className="flex-1">
                <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {demoProduct.name}
                </h4>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <Badge variant="outline" className="bg-white text-xs sm:text-sm">
                    Software
                  </Badge>
                  <span className="text-xl sm:text-2xl font-bold text-emerald-600">
                    {demoProduct.price}€/Jahr
                  </span>
                </div>
              </div>
              
              {demoProduct.status !== 'waiting' && (
                <Badge 
                  className={`text-xs sm:text-sm px-3 sm:px-4 py-2 whitespace-nowrap ${
                    demoProduct.status === 'bought' 
                      ? 'bg-blue-500 hover:bg-blue-500' 
                      : 'bg-green-500 hover:bg-green-500'
                  }`}
                >
                  {demoProduct.status === 'bought' ? 'Gekauft' : 'Gespart'}
                </Badge>
              )}
            </div>
                
            {/* Progress Section */}
              {demoProduct.status === 'waiting' && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-600">
                      {demoProduct.daysLeft} Tage verbleibend
                      </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {30 - demoProduct.daysLeft}/30 Tage
                  </span>
                    </div>
                
                <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                          demoProduct.daysLeft <= 7 ? 'bg-gradient-to-r from-red-400 to-red-500' :
                          demoProduct.daysLeft <= 14 ? 'bg-gradient-to-r from-orange-400 to-orange-500' :
                          'bg-gradient-to-r from-emerald-400 to-emerald-500'
                        }`}
                        style={{ width: `${((30 - demoProduct.daysLeft) / 30) * 100}%` }}
                      />
                  </div>
                </div>
              )}

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* Start Simulation */}
              {demoProduct.daysLeft === 30 && !demoProduct.isSimulating && !showResult && (
                <div className="text-center">
                  <Button
                    onClick={startSimulation}
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-8 text-sm sm:text-base"
                  >
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    30 Tage simulieren
                  </Button>
                </div>
              )}
              
              {/* Simulation Running */}
              {demoProduct.isSimulating && (
                <div className="text-center py-4">
                  <div className="animate-pulse text-emerald-600 font-medium">
                    Zeit vergeht...
                  </div>
                </div>
              )}
              
              {/* Decision Time */}
              {demoProduct.daysLeft === 0 && !demoProduct.isSimulating && !showResult && (
                <div className="space-y-4">
                  <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                         <h4 className="font-semibold text-yellow-800 mb-2">
                       30 Tage sind vorbei!
                     </h4>
                     <p className="text-yellow-700 text-sm">
                      Brauchst du KaufPause Premium wirklich oder war es nur ein Impuls?
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    <Button
                      onClick={() => handleDecision('bought')}
                      variant="outline"
                      className="text-blue-600 border-blue-600 hover:bg-blue-50 transition-all duration-300 w-full sm:w-auto"
                      data-decision="bought"
                    >
                      Kaufen
                    </Button>
                    <Button
                      onClick={() => handleDecision('skipped')}
                      variant="outline"
                      className="text-green-600 border-green-600 hover:bg-green-50 w-full sm:w-auto"
                      data-decision="skipped"
                    >
                      Sparen
                    </Button>
                  </div>
            </div>
          )}

          {/* Result */}
          {showResult && (
                <div className="space-y-4">
                  <div className={`text-center p-4 rounded-lg border ${
                    demoProduct.status === 'bought' 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <h4 className={`font-semibold mb-2 ${
                      demoProduct.status === 'bought' ? 'text-blue-800' : 'text-green-800'
                    }`}>
                      {demoProduct.status === 'bought' 
                        ? 'Bewusste Kaufentscheidung!' 
                        : 'Impulskauf vermieden!'}
                    </h4>
                    <p className={`text-sm ${
                      demoProduct.status === 'bought' ? 'text-blue-700' : 'text-green-700'
                    }`}>
                      {demoProduct.status === 'bought' 
                        ? 'Nach 30 Tagen Bedenkzeit hast du dich bewusst für den Kauf entschieden.' 
                        : `Du hast ${demoProduct.price}€ gespart! So funktioniert bewusstes Konsumieren.`}
                  </p>
                </div>
              
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={resetDemo} variant="outline" className="w-full sm:w-auto">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Nochmal testen
                    </Button>
                    <Button 
                      onClick={() => navigate('/demo')}
                      className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                    >
                      Vollständige Demo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
            </div>
          )}
            </div>
          </div>
        </CardContent>
      </Card>
  );
};
